import { Analytics } from '@cobliteam/analytics';
import type { Driver } from '@cobliteam/api-lib';
import type { DriverFaceImageAssignRequest } from '@cobliteam/common-types';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import DriverPhotosSelector from '@cobliteam/drivers/src/components/DriverPhotosSelector';
import { useDriverFaceCluster } from '@cobliteam/hooks';
import { useAssignDriverFaceImage } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import useConfirmBeforeLeave from '@cobliteam/hooks/src/useConfirmBeforeLeave';
import useDriver, { QUERY_KEY } from '@cobliteam/hooks/src/useEntity/useDriver';
import useIdentifierAssociations from '@cobliteam/hooks/src/useEntity/useIdentifierAssociations';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import { Alert, Divider, Drawer, DrawerBody, DrawerContent, DrawerFooter, Skeleton } from '@cobliteam/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { AlertDialogCancel } from '../../AlertDialog';
import DeleteDriverConfirmModal from './components/DeleteDriverConfirmModal';
import DriverDrawerHeader from './components/DriverDrawerHeader';
import { DriverFaceIdActiveCard } from './components/DriverFaceIdActiveCard/DriverFaceIdActiveCard';
import { DriverFaceIdEmptyBanner } from './components/DriverFaceIdEmptyBanner/DriverFaceIdEmptyBanner';
import DriverPhotosUpload from './components/DriverPhotosUpload';
import FooterButtons from './components/FooterButtons';
import { DRIVER_FACE_PHOTO_MAX } from './constants';
import DriverDrawerSkeleton from './DriverDrawerSkeleton';
import DriverForm, { type DriverFormRef } from './DriverForm';
import DriverImagesUpload, { type DriverImagesRef, type StagedFaceImageUpdate } from './DriverImagesUpload';
import type { DriverDrawerStep, DriverDrawerType } from './types';

export interface DriverDrawerProps {
  open: boolean;
  driver?: Driver;
  allDriversData: Driver[];
  isLoading: boolean;
  initialStep?: DriverDrawerStep;
  initialPickerOpen?: boolean;
  onCloseDrawer: () => void;
  onSuccess?: (createdDriver: Driver) => void;
  onSuggestionsClick?: (driverId: string) => void;
}

const newDriver: Driver = {
  id: '',
  fleetId: '',
  name: '',
  phoneNumbers: [''],
  identifier: '',
  identificationToken: undefined,
  driverCode: undefined,
  licenseCategory: undefined,
  firstLicenseAcquirementDateInMillis: undefined,
  licenseExpirationDateInMillis: undefined,
};

const MIN_REQUIRED_PHOTOS = 1;

const DriverDrawer = ({ open, driver = newDriver, onCloseDrawer, onSuccess, onSuggestionsClick, allDriversData, isLoading, initialStep, initialPickerOpen }: DriverDrawerProps) => {
  const { setPosition } = useChatWidget();
  const queryClient = useQueryClient();
  const { t } = useTranslation('drivers');

  const { runtimeFlags } = useDashboard();
  const hasCamera = runtimeFlags.hasCameraFlag;

  const { manualDriverImageAssign } = useFeatureFlags();

  const { isAccessAllowed } = usePermissions();
  const hasFaceIdPermission = isAccessAllowed('update:driver-face-cluster');
  const hasViewCameraMediaPermission = isAccessAllowed('view:camera-media');

  const { useDeleteDriver, useSaveDriver } = useDriver();
  const { mutate: deleteDriverHook, isPending: isDeletingDriver } = useDeleteDriver();
  const { mutateAsync: mutateSaveDriver, isPending: isSavingDriver } = useSaveDriver();

  const { mutateAsync: assignDriverFaceImage, isPending: isAssigningImages } = useAssignDriverFaceImage();

  const { associateTokenToDriver, disassociateTokenFromDriver } = useIdentifierAssociations.useIdentificationToken();
  const associateTokenToDriverMutation = associateTokenToDriver();
  const disassociateTokenFromDriverMutation = disassociateTokenFromDriver();

  const { getDriverFaceCluster } = useDriverFaceCluster();
  const { data: faceId, isLoading: isFaceIdLoading } = getDriverFaceCluster(driver.id, { enabled: hasCamera && hasViewCameraMediaPermission && !!driver.id });

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [step, setStep] = useState<DriverDrawerStep>(initialStep ?? 'form');
  const [_driver, _setDriver] = useState(driver);
  const [showAlertDialogCancel, setShowAlertDialogCancel] = useState(false);

  // Legacy upload staging (flag OFF)
  const [driverImages, setDriverImages] = useState<StagedFaceImageUpdate | null>(null);
  const driverImagesRef = useRef<DriverImagesRef>(null);
  // Captures the tokenId at the moment the drawer is opened, immune to re-fetches
  const originalTokenIdRef = useRef<string | undefined>(undefined);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingPhotosOpen, setPendingPhotosOpen] = useState(false);

  const driverFormRef = useRef<DriverFormRef>(null);

  const type: DriverDrawerType = driver.id ? 'edit' : 'add';
  const showFaceIdSection = type === 'edit' && hasCamera && hasViewCameraMediaPermission;
  const hasActiveFaceId = !!faceId?.profileImageUrl?.imageUrl && faceId?.sampleImageUrls?.length > 0;
  const existingExtraPhotosCount = faceId?.sampleImageUrls?.length ?? 0;
  const hasSufficientPhotos = existingExtraPhotosCount >= MIN_REQUIRED_PHOTOS;
  const canUploadManually = manualDriverImageAssign && hasCamera && hasFaceIdPermission;

  useEffect(() => {
    _setDriver(driver);
    if (open) {
      originalTokenIdRef.current = driver.identificationToken?.tokenId;
      setStep(initialStep ?? 'form');
      setDriverImages(null);
      setPickerOpen(initialPickerOpen ?? false);
    }
  }, [driver, open, initialStep, initialPickerOpen]);

  useEffect(() => setPosition(open ? 'drawer' : 'default'), [open, setPosition]);

  const saveDriverChanges = async (stagedDriver: Driver): Promise<Driver> => {
    Analytics.track(`Driver Drawer ${driver.id ? 'Update' : 'Save'} Button Clicked`);

    // Capture originalTokenId before mutateSaveDriver runs onMutate, which performs an
    // optimistic cache update replacing the driver with stagedDriver (tokenId = undefined).
    // This causes a re-render that updates the `driver` prop before we reach the
    // changedIdentificationToken check, making both sides undefined (false negative).
    // Reading from the ref here ensures we always compare against the value at drawer-open time.
    const originalTokenId = originalTokenIdRef.current;

    const savedDriver = await mutateSaveDriver(stagedDriver, {
      onSuccess: (d) => onSuccess?.(d),
    });

    const changedIdentificationToken = stagedDriver.identificationToken?.tokenId !== originalTokenId;

    if (changedIdentificationToken) {
      await handleSaveIdentificationToken(stagedDriver, savedDriver, originalTokenId);
    }

    if (driver.identificationToken?.tokenAlias !== stagedDriver.identificationToken?.tokenAlias) {
      Analytics.track('Driver Drawer - Token Alias Changed', {
        driverId: savedDriver.id,
        oldTokenAlias: driver.identificationToken?.tokenAlias,
        newTokenAlias: stagedDriver.identificationToken?.tokenAlias,
      });
    }
    return savedDriver;
  };

  const handleSaveIdentificationToken = async (stagedDriver: Driver, savedDriver: Driver, originalTokenId: string | undefined) => {
    if (stagedDriver.identificationToken?.tokenId === undefined && originalTokenId !== undefined) {
      await disassociateTokenFromDriverMutation.mutateAsync({ tokenId: originalTokenId });
    } else {
      await associateTokenToDriverMutation.mutateAsync({
        driverId: savedDriver.id,
        tokenId: stagedDriver.identificationToken?.tokenId || '',
      });
    }
  };

  // Legacy upload: save driver + apply staged image assignments (flag OFF only)
  const saveWithLegacyImages = async (stagedDriver: Driver, images: StagedFaceImageUpdate | null, driverChanged: boolean) => {
    let driverId = _driver.id;

    // Compare staged tokenId against the value captured when the drawer opened
    // (originalTokenIdRef), not against _driver which may have been updated by
    // background re-fetches with new object references but same values.
    const tokenChanged = stagedDriver.identificationToken?.tokenId !== originalTokenIdRef.current;

    if (driverChanged || tokenChanged) {
      try {
        const savedDriver = await saveDriverChanges(stagedDriver);
        driverId = savedDriver.id;
      } catch {
        // Save / token operation failed — onError in the mutation handles user feedback (toast).
        // Return early so the drawer stays open instead of calling onCloseDrawer().
        return;
      }
    }

    if (images && (images.changedAssignments.length > 0 || images.removedAssignments.length > 0)) {
      const changedAssignments: DriverFaceImageAssignRequest[] = images.changedAssignments.map((it) => ({
        driverId,
        url: it.url,
        useAsProfileImage: it.useAsProfileImage,
      }));
      const removedAssignments: DriverFaceImageAssignRequest[] = images.removedAssignments.map((it) => ({ driverId: null, url: it.url }));
      await assignDriverFaceImage(changedAssignments.concat(removedAssignments));

      if (!driverChanged) Alert.success(t('face_id.drawer.review.confirmed_toast'));
    }

    onCloseDrawer();
  };

  const onFormSubmit = () => driverFormRef.current?.handleSubmit();
  const onImagesSubmit = () => driverImagesRef.current?.handleSubmit();

  const onFormFinish = async (stagedDriver: Driver, hasChanges: boolean) => {
    _setDriver(stagedDriver);

    const hasStagedImages = !!driverImages && (driverImages.changedAssignments.length > 0 || driverImages.removedAssignments.length > 0);

    if (canUploadManually && hasStagedImages) {
      await saveWithLegacyImages(stagedDriver, driverImages, hasChanges);
      return;
    }

    if (hasChanges) {
      try {
        await saveDriverChanges(stagedDriver);
      } catch {
        setPendingPhotosOpen(false);
        return;
      }
    }

    if (pendingPhotosOpen) {
      setPendingPhotosOpen(false);
      setPickerOpen(true);
      return;
    }
    onCloseDrawer();
  };

  const onImagesFinish = async (imagesUpdates: StagedFaceImageUpdate) => {
    setDriverImages(imagesUpdates);

    if (type === 'add') {
      if (!_driver) return;
      await saveWithLegacyImages(_driver, imagesUpdates, true);
    }
    if (type === 'edit') {
      setStep('form');
    }
  };

  const onClickDeleteDriver = () => setOpenConfirmDeleteDialog(true);

  const deleteDriver = () => {
    deleteDriverHook(driver.id, {
      onSuccess: () => {
        Analytics.track('Driver Drawer Delete Success');
        queryClient.resetQueries({ queryKey: [QUERY_KEY] });
      },
      onError: () => Analytics.track('Driver Drawer Delete Error'),
    });
    onCloseDrawer();
    setOpenConfirmDeleteDialog(false);
  };

  const handleDelete = (values: Driver) => {
    if (values.identificationToken?.tokenId) {
      disassociateTokenFromDriverMutation.mutate({ tokenId: values.identificationToken.tokenId }, { onSuccess: () => deleteDriver() });
    } else {
      deleteDriver();
    }
    Analytics.track('Driver Drawer Delete Button Clicked');
  };

  const handlePhotosCta = () => {
    if (manualDriverImageAssign) {
      Analytics.track('Driver Drawer - Manage Images - Click');
      setStep('images');
      return;
    }
    if (hasSufficientPhotos) {
      setStep('images');
      return;
    }
    if (driverFormRef.current?.isDirty?.()) {
      setPendingPhotosOpen(true);
      driverFormRef.current?.handleSubmit?.();
      return;
    }
    setPickerOpen(true);
  };

  const handleManualUploadClick = () => {
    Analytics.track('Driver Drawer - Manage Images - Click');
    setStep('images');
  };

  const handleAddMorePhotos = () => {
    Analytics.track('Driver Drawer - Add More Photos - Click', { driverId: driver.id });
    setPickerOpen(true);
  };

  const handleClosePicker = () => setPickerOpen(false);

  const handlePhotosAssigned = () => {
    Alert.success(t('face_id.drawer.review.confirmed_toast'));
    setPickerOpen(false);
    onCloseDrawer();
  };

  const handleImagesBack = () => setStep('form');

  const handleImagesConfirm = () => onCloseDrawer();

  const hasImageChanges = (): boolean => {
    if (step === 'images') return driverImagesRef.current?.hasChanges?.() ?? false;
    if (!driverImages) return false;
    return driverImages.changedAssignments.length > 0 || driverImages.removedAssignments.length > 0;
  };

  const hasStagedChanges = () => canUploadManually && hasImageChanges();
  const hasUnsavedChanges = open && ((driverFormRef.current?.isDirty?.() ?? false) || hasStagedChanges());

  const { handleBlockedNavigation } = useConfirmBeforeLeave({ hasUnsavedChanges, onLeave: () => onCloseDrawer() });

  const requestCloseDrawer = () => {
    const formIsDirty = driverFormRef.current?.isDirty?.() ?? false;
    if (formIsDirty || hasStagedChanges()) {
      setShowAlertDialogCancel(true);
      return;
    }
    onCloseDrawer();
  };

  const handleCancel = () => {
    requestCloseDrawer();
    Analytics.track('Driver Drawer Cancel Button Clicked');
  };
  const handleCloseDrawer = () => {
    requestCloseDrawer();
    Analytics.track('Driver Drawer Close Button Clicked');
  };
  const handleConfirmDiscard = () => {
    setShowAlertDialogCancel(false);
    onCloseDrawer();
  };

  const remainingSlots = Math.max(0, DRIVER_FACE_PHOTO_MAX - existingExtraPhotosCount);

  const renderFaceIdSection = () => {
    if (isFaceIdLoading) {
      return <Skeleton height={132} className='rounded-lg' />;
    }
    if (hasActiveFaceId) {
      return <DriverFaceIdActiveCard driverId={driver.id} onManageClick={handlePhotosCta} disabled={!hasFaceIdPermission} />;
    }
    return <DriverFaceIdEmptyBanner onLinkPhotosClick={handlePhotosCta} disabled={!hasFaceIdPermission} />;
  };

  const renderImagesStep = () => {
    if (manualDriverImageAssign) {
      return (
        <DriverImagesUpload
          ref={driverImagesRef}
          driver={_driver}
          stagedImagesUpdate={driverImages}
          setCanSubmit={setCanSubmit}
          onFinish={onImagesFinish}
          onSuggestionsClick={onSuggestionsClick ? () => onSuggestionsClick(driver.id) : undefined}
        />
      );
    }
    return <DriverPhotosUpload driverId={driver.id} onAddMoreClusters={handleAddMorePhotos} disabled={!hasFaceIdPermission} />;
  };

  return (
    <>
      <Prompt when={hasUnsavedChanges} message={handleBlockedNavigation} />
      <Drawer open={open} onClose={handleCloseDrawer}>
        <DrawerContent>
          <DriverDrawerHeader type={type} title={step === 'images' && !manualDriverImageAssign ? t('face_id.drawer.review.images_step_title') : undefined} />
          <DrawerBody className='overflow-auto'>
            {isLoading ? (
              <DriverDrawerSkeleton closeDrawer={onCloseDrawer} />
            ) : (
              <>
                <div className='w-full space-y-5'>
                  {step === 'form' && (
                    <>
                      {showFaceIdSection && (
                        <>
                          {renderFaceIdSection()}
                          <Divider />
                        </>
                      )}
                      <DriverForm
                        ref={driverFormRef}
                        driver={_driver}
                        allDriversData={allDriversData}
                        isLoading={isSavingDriver}
                        setCanSubmit={setCanSubmit}
                        onFinish={onFormFinish}
                      />
                    </>
                  )}
                  {step === 'images' && renderImagesStep()}
                </div>
                <DeleteDriverConfirmModal
                  open={openConfirmDeleteDialog}
                  loading={isDeletingDriver}
                  driverName={driver.name}
                  onCancel={() => setOpenConfirmDeleteDialog(false)}
                  onConfirm={() => handleDelete(driver)}
                />
              </>
            )}
          </DrawerBody>
          <DrawerFooter>
            <FooterButtons
              step={step}
              type={type}
              canSubmit={canSubmit}
              manualMode={manualDriverImageAssign}
              isLoading={isSavingDriver || isAssigningImages}
              onFormDoneClick={onFormSubmit}
              onImagesDoneClick={onImagesSubmit}
              onImagesBack={handleImagesBack}
              onImagesConfirm={handleImagesConfirm}
              onCancelClickButton={handleCancel}
              onDeleteClickButton={onClickDeleteDriver}
            />
          </DrawerFooter>
        </DrawerContent>

        <AlertDialogCancel
          open={showAlertDialogCancel}
          title={t('close_drawer_without_saving.title')}
          description={t('close_drawer_without_saving.description')}
          confirmLabel={t('close_drawer_without_saving.confirm_label')}
          onOpenChange={setShowAlertDialogCancel}
          onConfirm={handleConfirmDiscard}
        />
      </Drawer>

      {!manualDriverImageAssign && pickerOpen && driver.id && (
        <DriverPhotosSelector
          open={pickerOpen}
          driverId={driver.id}
          driverName={driver.name}
          remainingSlots={remainingSlots}
          onClose={handleClosePicker}
          onAssigned={handlePhotosAssigned}
        />
      )}
    </>
  );
};

export default DriverDrawer;
